import {
	Component,
	ViewEncapsulation,
	HostBinding,
	Input,
	OnInit,
	OnDestroy,
	EventEmitter
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	ColDef,
	ICellRendererParams,
	ModuleRegistry,
	ViewportChangedEvent,
	GridReadyEvent,
	RowHeightParams,
	GridApi
} from '@ag-grid-community/core';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { Location } from '@angular/common';
import { Subscription, fromEvent } from 'rxjs';

import { ORGANIC_COATED_CATEGORY } from '../../../models/offer';
import { OFFER_TYPE } from '../../../models/offer-utils';
import { AppWizardService } from '../../../services/app-wizard.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { CommonColDef } from '../../../../shared/datacenter/column-defs';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { DataSource } from '../../../../shared/datacenter/data-source';
import { ProductListQuantityColumnDefinition, DEFAULT_VIEW_PRODUCT_LIST, ORGANIC_COATED_VIEW_PRODUCT_LIST }
	from '../../../../shared/datacenter/types/product-list/product-list-quantity-configuration';
import { UserDatacenterView } from '../../../../shared/models/datacenter-aggrid-view';
import {
	ProductListViewService
} from '../../../../shared/components/view-customization/services/product-list-view.service';
import { ColDefWithFields } from '../../../../shared/datacenter/column-type';
import { ClickIconCellComponent } from '../../../../shared/datacenter/cell-renderers/click-icon-cell.component';
import { EOfferProductData } from '../../../../shared/models/product';
import { DialogWarningComponent } from '../../../../shared/components/dialog-warning/dialog-warning.component';
import { FormProducts } from '../../../models/form-offer';

import { ItemQuantityColDef } from './col-def/product-list-col-def';

const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-offer-product-list-quantity',
	templateUrl: './product-list-quantity.component.html',
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './product-list-quantity.component.css',
	imports: [
		MatButtonModule,
		TranslateModule,
		MatRippleModule,
		MatIconModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		AgGridModule,
	]
})
export class ProductListQuantityComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() uniqueRefList!: string[];
	@Input() code!: string;
	@Input() category!: string;
	@Input() subCategory!: string;
	@Input() form!: string;
	@Input() formProducts!: FormProducts;
	@Input() isReadOnly = false;

	resizeSub?: Subscription;
	productRemovedSub?: Subscription;
	confirmationPopupSub: Subscription | undefined;
	agGridModule = clientSideAgGridModules;
	columnDefs: ColDef[] = [];
	qtyControlsMap = new Map<string, FormControl>();
	rowData: EOfferProductData[] = [];
	currentSelectedView: UserDatacenterView;
	dataGridReady = new EventEmitter<GridApi>();
	productListColumnDef: ProductListQuantityColumnDefinition;
	dataSource?: DataSource;
	gridApi!: GridApi;
	offerType: string | null | undefined;

	readonly gridOptions: CommonGridOptions<EOfferProductData>;

	constructor(
		private readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly viewService: ProductListViewService,
		readonly location: Location,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
	) {
		this.gridOptions = new CommonGridOptions<EOfferProductData>({
			translationService: this.translationService,
			suppressRowClickSelection: true,
			rowModelType: 'clientSide',
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			headerHeight: 70
		});
		ModuleRegistry.registerModules([RowGroupingModule]);
		this.currentSelectedView = DEFAULT_VIEW_PRODUCT_LIST;
		this.productListColumnDef = new ProductListQuantityColumnDefinition(
			translationService,
			productCharacteristicsTranslationService,
			NAMESPACE_RTPC_FIELD
		);
		const state = this.appWizardService.getState();
		this.offerType = state.offer.getOfferType() || sessionStorage.getItem(OFFER_TYPE);
		this.productRemovedSub = this.appWizardService.onProductRemoved.subscribe({
			next: () => {
				this.generateCodDefs();
			}
		});
	}

	ngOnInit() {
		let itemNumber = 1;
		for (const uniqueRef of this.uniqueRefList) {
			const product = this.appWizardService.getState().ui.getSimulationData(uniqueRef);
			if (product !== undefined) {
				product.itemNumber = itemNumber;
				this.rowData.push(product);
				this.qtyControlsMap.set(uniqueRef, new FormControl(this.getQuantityByUniqueRef(uniqueRef)));
				itemNumber++;
			}
		}
		if (this.rowData.length > 0) {
			const layoutKey = `EORDR_${ this.subCategory }_${ this.form }_layout`;
			this.currentSelectedView = this.getCurrentView(layoutKey, this.category);
			this.generateCodDefs();
		}
	}

	ngOnDestroy(): void {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		if (this.productRemovedSub !== undefined) {
			this.productRemovedSub.unsubscribe();
		}
		if (this.confirmationPopupSub !== undefined) {
			this.confirmationPopupSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent<EOfferProductData>) {
		this.gridApi = params.api;
		this.gridApi.setGridOption('columnDefs', this.columnDefs);
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	getRowHeight(_params: RowHeightParams): number | null | undefined {
		return 50;
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	generateCodDefs() {
		this.columnDefs = [
			new CommonColDef<EOfferProductData>({
				headerComponent: CheckboxHeaderComponent,
				headerName: this.translationService.getTR('eorder.wizard.product.itemnumber'),
				sortable: false,
				minWidth: 100,
				maxWidth: 100,
				width: 100,
				cellStyle: { display: 'flex', alignItems: 'center' },
				valueGetter: params => params.data?.itemNumber
					? this.getPreviousFamiliesItemCount(params.data.itemNumber)
					: '000'
			}),
			new CommonColDef<EOfferProductData>({
				headerComponent: CheckboxHeaderComponent,
				sortable: false,
				headerName: this.translationService.getTR('eorder.grid.header.view.label'),
				minWidth: 50,
				maxWidth: 50,
				width: 50,
				cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
				cellRenderer: ClickIconCellComponent,
				cellRendererParams: {
					iconName: 'icon-eye',
					onIconClick: () => undefined,
				},
				tooltipValueGetter: () => this.translationService.getTR('eoffer.product.card')
			})
		];
		if (this.offerType === 'spot') {
			if (this.isReadOnly) {
				this.columnDefs.push(new CommonColDef<EOfferProductData>({
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('eorder.wizard.product.quantity'),
					sortable: false,
					minWidth: 125,
					maxWidth: 125,
					width: 125,
					cellStyle: { display: 'flex', alignItems: 'center' },
					valueGetter: params => {
						if (params.data?.uniqueRef) {
							return this.getQuantityByUniqueRef(params.data.uniqueRef);
						}
						return '';
					}
				}));
			} else {
				this.columnDefs.push(new ItemQuantityColDef<EOfferProductData>({
					sortable: false,
					minWidth: 150,
					maxWidth: 150,
					width: 150,
					cellStyle: { display: 'flex', alignItems: 'center' },
					cellRendererParams: {
						inputParams: (params: ICellRendererParams<EOfferProductData, unknown>) => {
							if (params.data?.uniqueRef) {
								const qtyControl = this.qtyControlsMap.get(params.data.uniqueRef);
								if (qtyControl) {
									return {
										uniqueRef: params.data.uniqueRef,
										qtyControl: this.qtyControlsMap.get(params.data.uniqueRef)
									};
								}
							}
							return {};
						},
						onChangeItemQuantity: (uniqueRef: string, quantity: number) => {
							this.updateQuantity(uniqueRef, quantity);
						}
					}
				}));
			}
		}
		if (!this.isReadOnly) {
			this.columnDefs.push(new CommonColDef<EOfferProductData>({
				headerComponent: CheckboxHeaderComponent,
				sortable: false,
				minWidth: 50,
				maxWidth: 50,
				width: 50,
				cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
				cellRenderer: ClickIconCellComponent,
				cellRendererParams: {
					iconName: 'garbage',
					onIconClick: (params: ICellRendererParams<EOfferProductData>) => {
						this.removeItem(params);
						return undefined;
					},
				},
				tooltipValueGetter: () => this.translationService.getTR('eoffer.product.card')
			}));
		}
		this.createColumnDefs();
	}

	checkError(withFocus: boolean) {
		let indexInvalid = -1;

		let i = 0;
		for (const product of this.rowData) {
			if (product.uniqueRef) {
				const qtyControl = this.qtyControlsMap.get(product.uniqueRef);
				qtyControl?.markAsTouched();
				if (qtyControl?.invalid && indexInvalid === -1) {
					indexInvalid = i;
				}
			}
			i += 1;
		}
		if (withFocus && indexInvalid > -1) {
			this.gridApi.setFocusedCell(indexInvalid, 'quantity');
		}
		return indexInvalid > -1 ? this.code : '';
	}

	private removeItem(params: ICellRendererParams<EOfferProductData>) {
		const dialogRef = this.dialog.open(DialogWarningComponent, {
			data: {
				iconName: 'alert',
				title: 'global.warning',
				contents: [this.translationService.getTR('eoffer.dialog.offer.remove.product')],
				cancelButtonEnabled: true
			}
		});
		this.confirmationPopupSub = dialogRef.afterClosed()
			.subscribe(result => {
				if (result) {
					if (params.data?.uniqueRef) {
						this.formProducts.removeProductByUniqueRef(params.data.uniqueRef);
						const productCount = this.appWizardService.productsGroupedByFamily.get(this.code);
						if (productCount) {
							this.appWizardService.productsGroupedByFamily.set(this.code, productCount - 1);
						}
						if (this.formProducts.length < 1) {
							const stateofoffer = this.appWizardService.getStateOfferForm();
							stateofoffer.getFormFamilyByVolumes().removeFamilyByVolume(this.code);
						}
						let i = this.rowData.findIndex(product => product.uniqueRef === params.data?.uniqueRef);
						if (i !== -1) {
							const removedRow = this.rowData[i];
							this.rowData.splice(i, 1);
							while (i < this.rowData.length) {
								this.rowData[i].itemNumber = i + 1;
								i++;
							}
							params.api.applyTransaction({ remove: [removedRow] });
							params.api.refreshCells();
							this.appWizardService.onProductRemoved.emit();
						}
					}
				} else {
					this.openSnackBar(this.translationService.getTR('global.action.cancelled'));
				}
			});
	}

	private getQuantityByUniqueRef(uniqueRef?: string) {
		if (uniqueRef) {
			const formProduct = this.appWizardService.getStateOfferForm().getFormProducts().getFormProduct(uniqueRef);
			if (formProduct) {
				return <number>formProduct.getFormQuantity().value;
			}
		}
		return undefined;
	}

	private updateQuantity(
		uniqueRef: string,
		quantity: number
	) {
		const formProduct = this.appWizardService.getStateOfferForm().getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			formProduct.getFormQuantity().setValue(quantity);
		}
	}

	private getCurrentView(layoutKey: string, categoryCode?: string) {
		const allColumns = <ColDefWithFields[]> Array.from(
			this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().values()
		);
		const colIds: string[] = allColumns.map(item => item.colId ?? '').filter(colId => colId !== '');
		return this.viewService.getCurrentViewByKey(
			categoryCode === ORGANIC_COATED_CATEGORY ? ORGANIC_COATED_VIEW_PRODUCT_LIST : DEFAULT_VIEW_PRODUCT_LIST,
			'product-list',
			layoutKey,
			colIds
		);
	}

	private createColumnDefs() {
		let allColumns = <ColDefWithFields[]> Array.from(
			this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().values()
		);
		const groupIds = [
			...new Set(allColumns.filter(item => item.groupId !== undefined)
				.map(item => item.groupId ?? ''))
		];
		if (groupIds.length > 0) {
			allColumns = allColumns.filter(allcol => groupIds.length > 0
				&& !groupIds.includes(allcol.colId ?? ''));
		}

		let cols = this.viewService.convertViewToColumnDefinitions(this.currentSelectedView, allColumns);
		cols = cols.map(colDef => ({
			...colDef,
			width: colDef.width !== undefined ? colDef.width : undefined,
			minWidth: colDef.minWidth ?? 0,
			maxWidth: colDef.maxWidth !== undefined ? colDef.maxWidth : undefined,
			cellStyle: { display: 'flex', alignItems: 'center' },
			headerComponent: CheckboxHeaderComponent,
			cellRendererSelector: (params: ICellRendererParams<unknown>) =>
				!params.value
					? {
						component: undefined,
						params: undefined
					}
					: {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						component: colDef.cellRenderer,
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						params: colDef.cellRendererParams
					}
		}));
		for (const col of cols) {
			this.columnDefs.push(col);
		}
	}

	private getPreviousFamiliesItemCount(itemNumber: number) {
		const finalItemNumber = itemNumber + this.appWizardService.getPreviousFamiliesItemCount(this.code);
		return finalItemNumber.toString().padStart(3, '0');
	}

	private openSnackBar(message: string) {
		this.snackBar.open(message, undefined, { duration: 5000 });
	}
}
